import React, { useState } from 'react';
import {
  Plus, Edit3, Trash2, Save, X, Layers, BookOpen, FileQuestion, Award, ChevronDown, ChevronRight, RefreshCw, Pencil,
} from 'lucide-react';
import { ref, set } from 'firebase/database';
import { database } from '../../firebase';
import { QuestionEditor } from './QuestionEditor';
import { FlashcardsEditor } from './FlashcardsEditor';
import { enrichCourseWithResumen } from '../../services/academyFirestore';
import { localCourseResumen } from '../../utils/courseStructure';

const S = {
  card: {
    background: 'rgba(10, 25, 41, 0.6)', border: '1px solid rgba(197,160,89,0.18)',
    borderRadius: '14px', overflow: 'hidden',
  },
  label: {
    fontSize: '0.7rem', fontWeight: 700, color: '#c5a059', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: '4px', display: 'block',
  },
  input: {
    width: '100%', background: 'rgba(7, 21, 38, 0.8)', border: '1px solid rgba(197,160,89,0.25)',
    borderRadius: '8px', padding: '8px 12px', fontSize: '0.8rem', color: '#e2e8f0',
    outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif",
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #c5a059 0%, #8a6a2e 100%)', color: '#fff', border: 'none',
    borderRadius: '10px', padding: '9px 18px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  btnSecondary: {
    background: 'rgba(197,160,89,0.1)', color: '#c5a059', border: '1px solid rgba(197,160,89,0.3)',
    borderRadius: '10px', padding: '8px 14px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  btnDanger: {
    background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '10px', padding: '8px 12px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
  },
  badge: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '20px',
    fontSize: '0.65rem', fontWeight: 700, background: `${color}22`, color, border: `1px solid ${color}44`,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }),
};

const emptyLesson = () => ({
  id: `l_${Date.now()}`, title: '', module: '', video_url: '',
  audio_url: '', audio_caption: '', images: [],
  duration_seconds: 360, key_points: [], pdf_resources: [], quiz: [], examenes: [], flashcards: [],
});

const emptyExam = () => ({
  id: `e_${Date.now()}`, title: '', order: 1, quizId: '', questions: [],
});

const ModalShell = ({ title, icon, onClose, onSubmit, submitLabel, children }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    background: 'rgba(4,12,22,0.85)', backdropFilter: 'blur(10px)',
  }}>
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      style={{
        background: '#0a1929', border: '1px solid rgba(197,160,89,0.3)', borderRadius: '18px',
        width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 22px', borderBottom: '1px solid rgba(197,160,89,0.15)',
        position: 'sticky', top: 0, background: '#0a1929', zIndex: 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#fff' }}>{title}</h3>
        </div>
        <button type="button" onClick={onClose}  className="adm-btn adm-btn--secondary">
          <X size={20} />
        </button>
      </div>

      <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {children}
      </div>

      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: '10px',
        padding: '16px 22px', borderTop: '1px solid rgba(197,160,89,0.15)',
        position: 'sticky', bottom: 0, background: '#0a1929',
      }}>
        <button type="button" onClick={onClose} className="adm-btn adm-btn--secondary">Cancelar</button>
        <button type="submit" className="adm-btn adm-btn--primary">
          <Save size={14} />
          <span>{submitLabel}</span>
        </button>
      </div>
    </form>
  </div>
);

export const AdminCursoContenido = ({ course, onSaved }) => {
  const [expandedTemas, setExpandedTemas] = useState({});

  // Modal Tema
  const [temaModal, setTemaModal] = useState(null); // { editing: bool, title, order }
  // Modal Lección
  const [lessonModal, setLessonModal] = useState(null); // { temaId, editing: bool, lesson }
  // Modal Examen
  const [examModal, setExamModal] = useState(null); // { temaId, editing: bool, exam }
  // Modal Examen Final del Tema
  const [finalTemaModal, setFinalTemaModal] = useState(null); // { temaId, editing: bool, exam }

  const temas = course.temas || [];
  // Resumen real: viene enriquecido (Firestore) o se calcula localmente.
  const resumen = course.resumen || localCourseResumen(course);

  // ── Override manual del resumen (conteos que ve el usuario) ──
  const [editingResumen, setEditingResumen] = useState(false);
  const [resumenForm, setResumenForm] = useState({
    temas: resumen.temas,
    lecciones: resumen.lecciones,
    miniExamenes: resumen.miniExamenes,
    examenes: resumen.examenes,
    preguntas: resumen.preguntas,
  });
  const [recalcLoading, setRecalcLoading] = useState(false);

  const persist = async (updatedCourse) => {
    const { resumen: _r, areaKey: _ak, areaTemas: _at, ...cleanCourse } = updatedCourse;
    const data = { ...cleanCourse, updatedAt: Date.now() };
    await set(ref(database, `vortex_web_courses/${course.id}`), data);
    if (onSaved) onSaved({ ...data, resumen, areaKey: course.areaKey, areaTemas: course.areaTemas });
  };

  // Recalcula el resumen automáticamente desde Firestore vortexAcademyAreas.
  const handleRecalcular = async () => {
    setRecalcLoading(true);
    try {
      const enriched = await enrichCourseWithResumen(course);
      const r = enriched.resumen || localCourseResumen(enriched);
      setResumenForm({
        temas: r.temas, lecciones: r.lecciones,
        miniExamenes: r.miniExamenes, examenes: r.examenes, preguntas: r.preguntas,
      });
      setEditingResumen(false);
      if (onSaved) onSaved({ ...course, resumen: r, areaKey: enriched.areaKey, areaTemas: enriched.areaTemas });
    } catch (e) {
      console.error('[AdminCursoContenido] Recalcular:', e);
    } finally {
      setRecalcLoading(false);
    }
  };

  // Guarda los conteos fijados manualmente en RTDB como resumen_override.
  const handleSaveResumen = async () => {
    const nums = (v) => Math.max(0, parseInt(v, 10) || 0);
    const override = {
      temas: nums(resumenForm.temas),
      lecciones: nums(resumenForm.lecciones),
      miniExamenes: nums(resumenForm.miniExamenes),
      examenes: nums(resumenForm.examenes),
      preguntas: nums(resumenForm.preguntas),
    };
    const updated = { ...course, resumen_override: override };
    await persist(updated);
    setEditingResumen(false);
  };

  // Quita el override manual (vuelve al conteo automático de Firestore).
  const handleClearOverride = async () => {
    const { resumen_override, ...rest } = course;
    await persist({ ...rest, resumen_override: null });
  };

  const toggleTema = (id) => setExpandedTemas((prev) => ({ ...prev, [id]: !prev[id] }));

  /* ── Tema ── */
  const saveTema = async () => {
    if (!temaModal.title.trim()) return;
    if (temaModal.editing) {
      const updated = { ...course, temas: temas.map((t) => (t.id === temaModal.tid ? { ...t, title: temaModal.title, order: parseInt(temaModal.order) || t.order } : t)) };
      await persist(updated);
    } else {
      const newTema = { id: `t_${Date.now()}`, title: temaModal.title, order: temas.length + 1, lessons: [], examenes: [], examen_final: null };
      await persist({ ...course, temas: [...temas, newTema] });
    }
    setTemaModal(null);
  };

  const deleteTema = async (temaId) => {
    if (!window.confirm('¿Eliminar este tema con todas sus lecciones y exámenes?')) return;
    await persist({ ...course, temas: temas.filter((t) => t.id !== temaId) });
  };

  /* ── Lección ── */
  const saveLesson = async () => {
    const { temaId, lesson } = lessonModal;
    if (!lesson.title.trim()) return;
    const target = temas.find((t) => t.id === temaId);
    const existing = target.lessons || [];
    const updatedLessons = lessonModal.editing
      ? existing.map((l) => (l.id === lesson.id ? lesson : l))
      : [...existing, { ...lesson, id: lesson.id || `l_${Date.now()}` }];
    await persist({
      ...course,
      temas: temas.map((t) => (t.id === temaId ? { ...t, lessons: updatedLessons } : t)),
    });
    setLessonModal(null);
  };

  const deleteLesson = async (temaId, lessonId) => {
    if (!window.confirm('¿Eliminar esta lección?')) return;
    const target = temas.find((t) => t.id === temaId);
    await persist({
      ...course,
      temas: temas.map((t) => (t.id === temaId ? { ...t, lessons: (target.lessons || []).filter((l) => l.id !== lessonId) } : t)),
    });
  };

  /* ── Examen de lección ── */
  const saveExam = async () => {
    const { temaId, lessonId, exam } = examModal;
    if (!exam.title.trim()) return;
    const target = temas.find((t) => t.id === temaId);
    const cleanQuestions = (exam.questions || []).filter((q) => q.question.trim());
    const updateLessons = (tema) =>
      tema.lessons.map((l) => {
        if (l.id !== lessonId) return l;
        const examenes = l.examenes || [];
        const updatedExams = examModal.editing
          ? examenes.map((e) => (e.id === exam.id ? { ...exam, questions: cleanQuestions } : e))
          : [...examenes, { ...exam, id: exam.id || `e_${Date.now()}`, order: examenes.length + 1, questions: cleanQuestions }];
        return { ...l, examenes: updatedExams };
      });
    await persist({
      ...course,
      temas: temas.map((t) => (t.id === temaId ? { ...t, lessons: updateLessons(t) } : t)),
    });
    setExamModal(null);
  };

  const deleteExam = async (temaId, lessonId, examId) => {
    if (!window.confirm('¿Eliminar este examen de la lección?')) return;
    const target = temas.find((t) => t.id === temaId);
    const updateLessons = (tema) =>
      tema.lessons.map((l) => {
        if (l.id !== lessonId) return l;
        return { ...l, examenes: (l.examenes || []).filter((e) => e.id !== examId) };
      });
    await persist({
      ...course,
      temas: temas.map((t) => (t.id === temaId ? { ...t, lessons: updateLessons(t) } : t)),
    });
  };

  /* ── Examen Final ── */
  /* ── Examen Final del Tema ── */
  const saveFinalTema = async () => {
    const { temaId, exam } = finalTemaModal;
    if (!exam.title.trim()) return;
    const cleanQuestions = (exam.questions || []).filter((q) => q.question.trim());
    const examFinal = { id: exam.id || `tf_${Date.now()}`, title: exam.title, questions: cleanQuestions };
    await persist({
      ...course,
      temas: temas.map((t) => (t.id === temaId ? { ...t, examen_final: examFinal } : t)),
    });
    setFinalTemaModal(null);
  };

  const deleteFinalTema = async (temaId) => {
    if (!window.confirm('¿Eliminar el examen final de este tema?')) return;
    await persist({
      ...course,
      temas: temas.map((t) => (t.id === temaId ? { ...t, examen_final: null } : t)),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Encabezado del curso + Examen Final */}
      <div style={{ ...S.card, padding: '16px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        <BookOpen size={16} style={{ color: '#c5a059' }} />
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', flex: 1, minWidth: 0 }}>
          {course.title}
        </span>
        <span style={S.badge('#c5a059')}>{resumen.temas} temas</span>
        <span style={S.badge('#d8b978')}>{resumen.lecciones} lecciones</span>
        <span style={S.badge('#f59e0b')}>{resumen.miniExamenes} mini exámenes</span>
        <span style={S.badge('#34d399')}>{resumen.examenes} exámenes</span>
        <span style={S.badge(resumen.source === 'manual' ? '#c5a059' : '#64748b')}>
          {resumen.source === 'manual' ? '✏️ Conteo manual' : '🤖 Conteo automático'}
        </span>
      </div>

      {/* Panel de resumen: recalcular automático / override manual */}
      <div style={{ ...S.card, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
          <p style={{ margin: 0, flex: 1, minWidth: 220, fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.6 }}>
            El resumen del curso (lo que ve el alumno en el catálogo y en el temario completo) se
            calcula <strong style={{ color: '#c5a059' }}>automáticamente</strong> verificando cada tema, lección y
            evaluación del área en Firestore (<code style={{ color: '#c5a059' }}>vortexAcademyAreas</code>).
            Puedes recalcularlo o fijar los conteos manualmente.
          </p>
          <button onClick={handleRecalcular} disabled={recalcLoading} className="adm-btn adm-btn--secondary">
            <RefreshCw size={13} />
            <span>{recalcLoading ? 'Calculando...' : 'Recalcular automático'}</span>
          </button>
          {!editingResumen ? (
            <button onClick={() => setEditingResumen(true)} className="adm-btn adm-btn--primary">
              <Pencil size={13} />
              <span>Fijar manualmente</span>
            </button>
          ) : (
            <button onClick={() => setEditingResumen(false)} className="adm-btn adm-btn--secondary">
              <X size={13} />
              <span>Cancelar</span>
            </button>
          )}
          {course.resumen_override && (
            <button onClick={handleClearOverride}           className="adm-btn adm-btn--danger">
              <Trash2 size={13} />
              <span>Quitar conteo manual</span>
            </button>
          )}
        </div>

        {editingResumen && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end',
            padding: '12px', borderRadius: '10px', background: 'rgba(7,21,38,0.5)',
            border: '1px solid rgba(197,160,89,0.2)',
          }}>
            {[
              { key: 'temas', label: 'Temas' },
              { key: 'lecciones', label: 'Lecciones' },
              { key: 'miniExamenes', label: 'Mini Exámenes' },
              { key: 'examenes', label: 'Exámenes' },
              { key: 'preguntas', label: 'Preguntas' },
            ].map((f) => (
              <div key={f.key} style={{ minWidth: '120px', flex: 1 }}>
                <label style={S.label}>{f.label}</label>
                <input
                  type="number"
                  min={0}
                  value={resumenForm[f.key]}
                  onChange={(e) => setResumenForm({ ...resumenForm, [f.key]: e.target.value })}
                  className="adm-input"
                />
              </div>
            ))}
            <button onClick={handleSaveResumen}  style={{  ...S.btnPrimary,  alignSelf: 'center'  }} className="adm-btn adm-btn--secondary">
              <Save size={13} />
              <span>Guardar conteo manual</span>
            </button>
          </div>
        )}
      </div>

      {/* Botón nuevo tema */}
      <button
        onClick={() => setTemaModal({ editing: false, title: '', order: temas.length + 1 })}
        style={{ ...S.btnPrimary, alignSelf: 'flex-start' }}
      >
        <Plus size={14} />
        <span>Agregar Tema</span>
      </button>

      {/* Lista de temas */}
      {temas.length === 0 ? (
        <div style={{ ...S.card, padding: '3rem', textAlign: 'center' }}>
          <Layers size={36} style={{ color: '#c5a059', opacity: 0.35 }} />
          <p style={{ margin: '12px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
            No hay temas en este curso. Agrega el primer tema para comenzar.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {temas.map((tema, tIdx) => {
            const isExpanded = expandedTemas[tema.id];
            return (
              <div key={tema.id} style={{ ...S.card }}>
                {/* Header tema */}
                <div style={{
                  padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px',
                  cursor: 'pointer', background: 'rgba(7,21,38,0.4)',
                }} onClick={() => toggleTema(tema.id)}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                    background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.78rem', fontWeight: 800, color: '#c5a059',
                  }}>
                    {tIdx + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>{tema.title}</p>
                    <span style={{ fontSize: '0.68rem', color: '#64748b' }}>
                      {tema.lessons?.length || 0} lecciones · {tema.lessons?.reduce((a, l) => a + (l.examenes?.length || 0), 0) || 0} exámenes
                    </span>
                  </div>
                  {isExpanded ? <ChevronDown size={16} style={{ color: '#64748b' }} /> : <ChevronRight size={16} style={{ color: '#64748b' }} />}
                </div>

                {isExpanded && (
                  <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid rgba(197,160,89,0.12)' }}>
                    {/* Acciones tema */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      <button
                        onClick={() => setTemaModal({ editing: true, tid: tema.id, title: tema.title, order: tema.order })}
                        className="adm-btn adm-btn--secondary"
                      >
                        <Edit3 size={12} /> <span>Editar Tema</span>
                      </button>
                      <button
                        onClick={() => setLessonModal({ temaId: tema.id, editing: false, lesson: emptyLesson() })}
                        className="adm-btn adm-btn--primary"
                      >
                        <Plus size={12} /> <span>Agregar Lección</span>
                      </button>
                      <button
                        onClick={() => setFinalTemaModal({
                          temaId: tema.id,
                          editing: !!tema.examen_final,
                          exam: {
                            id: tema.examen_final?.id || `tf_${Date.now()}`,
                            title: tema.examen_final?.title || `Examen Final de ${tema.title}`,
                            questions: (tema.examen_final?.questions || []).map((q) => ({ ...q, options: [...q.options] })),
                          },
                        })}
                        style={{ ...S.btnSecondary, borderColor: 'rgba(52,211,153,0.4)', color: '#34d399' }}
                      >
                        <Award size={12} /> <span>{tema.examen_final ? 'Editar Examen Final' : 'Examen Final del Tema'}</span>
                      </button>
                      {tema.examen_final && (
                        <button onClick={() => deleteFinalTema(tema.id)}           className="adm-btn adm-btn--danger">
                          <Trash2 size={12} /> <span>Quitar Examen Final</span>
                        </button>
                      )}
                      <button onClick={() => deleteTema(tema.id)} style={{ ...S.btnDanger, marginLeft: 'auto' }}>
                        <Trash2 size={12} /> <span>Eliminar Tema</span>
                      </button>
                    </div>

                    {/* Lecciones (cada una con sus exámenes debajo) */}
                    {(tema.lessons || []).length > 0 && (
                      <div>
                        <p style={{ ...S.label, marginBottom: '8px' }}>Lecciones y sus Exámenes</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {(tema.lessons || []).map((lesson, li) => {
                            const lessonExams = lesson.examenes || [];
                            return (
                              <div key={lesson.id || li} style={{
                                borderRadius: '10px', overflow: 'hidden',
                                border: '1px solid rgba(197,160,89,0.2)', background: 'rgba(7,21,38,0.4)',
                              }}>
                                {/* Fila lección */}
                                <div style={{
                                  padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                                  background: 'rgba(7,21,38,0.6)',
                                }}>
                                  <BookOpen size={13} style={{ color: '#c5a059', flexShrink: 0 }} />
                                  <span style={{ flex: 1, minWidth: 0, fontSize: '0.76rem', color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {lesson.title}
                                  </span>
                                  <span style={S.badge('#f59e0b')}>{lessonExams.length} exam</span>
                                  <button
                                    onClick={() => setExamModal({ temaId: tema.id, lessonId: lesson.id, editing: false, exam: emptyExam() })}
                                    style={{ ...S.btnSecondary, padding: '5px 10px', borderColor: 'rgba(245,158,11,0.4)', color: '#f59e0b', background: 'rgba(245,158,11,0.07)' }}
                                    title="Agregar examen a esta lección"
                                  >
                                    <Plus size={12} /> <span>Examen</span>
                                  </button>
                                  <button
                                    onClick={() => setLessonModal({ temaId: tema.id, editing: true, lesson: { ...lesson, key_points: [...(lesson.key_points || [])], pdf_resources: [...(lesson.pdf_resources || [])], quiz: [...(lesson.quiz || [])].map((q) => ({ ...q, options: [...q.options] })) } })}
                                    style={{ ...S.btnSecondary, padding: '5px 10px' }}
                                  >
                                    <Edit3 size={12} />
                                  </button>
                                  <button onClick={() => deleteLesson(tema.id, lesson.id)} style={{ ...S.btnDanger, padding: '5px 10px' }}>
                                    <Trash2 size={12} />
                                  </button>
                                </div>

                                {/* Exámenes de la lección */}
                                {lessonExams.length > 0 && (
                                  <div style={{ padding: '8px 12px 8px 34px', display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid rgba(197,160,89,0.1)' }}>
                                    {lessonExams.map((exam, ei) => (
                                      <div key={exam.id || ei} style={{
                                        padding: '8px 10px', borderRadius: '8px', background: 'rgba(7,21,38,0.5)',
                                        border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', gap: '10px',
                                      }}>
                                        <FileQuestion size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                        <span style={{ flex: 1, minWidth: 0, fontSize: '0.74rem', color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {exam.title}
                                        </span>
                                        <span style={S.badge('#f59e0b')}>{exam.questions?.length || 0} preg</span>
                                        <button
                                          onClick={() => setExamModal({ temaId: tema.id, lessonId: lesson.id, editing: true, exam: { ...exam, questions: (exam.questions || []).map((q) => ({ ...q, options: [...q.options] })) } })}
                                          style={{ ...S.btnSecondary, padding: '5px 10px', borderColor: 'rgba(245,158,11,0.4)', color: '#f59e0b' }}
                                        >
                                          <Edit3 size={12} />
                                        </button>
                                        <button onClick={() => deleteExam(tema.id, lesson.id, exam.id)} style={{ ...S.btnDanger, padding: '5px 10px' }}>
                                          <Trash2 size={12} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── MODAL TEMA ── */}
      {temaModal && (
        <ModalShell
          title={temaModal.editing ? 'Editar Tema' : 'Agregar Tema'}
          icon={<Layers size={16} style={{ color: '#c5a059' }} />}
          onClose={() => setTemaModal(null)}
          onSubmit={saveTema}
          submitLabel={temaModal.editing ? 'Guardar Tema' : 'Agregar Tema'}
        >
          <div>
            <label style={S.label} htmlFor="tema-title">Título del Tema</label>
            <input
              autoFocus
              id="tema-title"
              type="text"
              value={temaModal.title}
              onChange={(e) => setTemaModal({ ...temaModal, title: e.target.value })}
              className="adm-input"
              placeholder="Ej: Tema 1: Fundamentos Legales"
            />
          </div>
          <div>
            <label style={S.label} htmlFor="tema-order">Orden</label>
            <input
              id="tema-order"
              type="number"
              value={temaModal.order}
              onChange={(e) => setTemaModal({ ...temaModal, order: e.target.value })}
              className="adm-input"
            />
          </div>
        </ModalShell>
      )}

      {/* ── MODAL LECCIÓN ── */}
      {lessonModal && (
        <ModalShell
          title={lessonModal.editing ? 'Editar Lección' : 'Agregar Lección'}
          icon={<BookOpen size={16} style={{ color: '#c5a059' }} />}
          onClose={() => setLessonModal(null)}
          onSubmit={saveLesson}
          submitLabel={lessonModal.editing ? 'Guardar Lección' : 'Agregar Lección'}
        >
          <div>
            <label style={S.label} htmlFor="leccion-title">Título de la Lección</label>
            <input
              autoFocus
              id="leccion-title"
              type="text"
              value={lessonModal.lesson.title}
              onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, title: e.target.value } })}
              className="adm-input"
              placeholder="Ej: Ley de Disciplina Naval - Capítulo I"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={S.label} htmlFor="leccion-module">Módulo / Subtema</label>
              <input
                id="leccion-module"
                type="text"
                value={lessonModal.lesson.module}
                onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, module: e.target.value } })}
                className="adm-input"
                placeholder="Módulo 1"
              />
            </div>
            <div>
              <label style={S.label} htmlFor="leccion-duracion">Duración (segundos)</label>
              <input
                id="leccion-duracion"
                type="number"
                value={lessonModal.lesson.duration_seconds}
                onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, duration_seconds: e.target.value } })}
                className="adm-input"
              />
            </div>
          </div>
          <div>
            <label style={S.label} htmlFor="leccion-video">URL de Video (MP4 / HLS)</label>
            <input
              id="leccion-video"
              type="url"
              value={lessonModal.lesson.video_url || ''}
              onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, video_url: e.target.value } })}
              className="adm-input"
              placeholder="https://..."
            />
          </div>
          <div>
            <label style={S.label} htmlFor="leccion-audio">Podcast / Audio de Apoyo (mp3)</label>
            <input
              id="leccion-audio"
              type="url"
              value={lessonModal.lesson.audio_url || ''}
              onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, audio_url: e.target.value } })}
              className="adm-input"
              placeholder="https://.../podcast.mp3"
            />
            <input
              id="leccion-audio-caption"
              type="text"
              value={lessonModal.lesson.audio_caption || ''}
              onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, audio_caption: e.target.value } })}
              style={{ ...S.input, marginTop: '8px' }}
              placeholder="Título / encabezado del podcast (opcional)"
            />
          </div>
          <div>
            <label style={S.label}>Imágenes de Apoyo (URL + pie)</label>
            {(lessonModal.lesson.images || []).map((im, ii) => (
              <div key={im.id || ii} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={im.url || ''}
                  onChange={(e) => {
                    const list = [...(lessonModal.lesson.images || [])];
                    list[ii] = { ...list[ii], url: e.target.value };
                    setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, images: list } });
                  }}
                  style={{ ...S.input, flex: 1 }}
                  placeholder="https://.../imagen.png"
                />
                <input
                  type="text"
                  value={im.caption || ''}
                  onChange={(e) => {
                    const list = [...(lessonModal.lesson.images || [])];
                    list[ii] = { ...list[ii], caption: e.target.value };
                    setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, images: list } });
                  }}
                  style={{ ...S.input, width: '180px' }}
                  placeholder="Pie / descripción"
                />
                <button
                  type="button"
                  onClick={() => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, images: (lessonModal.lesson.images || []).filter((_, x) => x !== ii) } })}
                  style={{ ...S.btnDanger, padding: '6px 10px' }}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, images: [...(lessonModal.lesson.images || []), { url: '', caption: '' }] } })}
              style={{ ...S.btnSecondary, borderColor: 'rgba(197,160,89,0.4)', color: '#c5a059' }}
            >
              <Plus size={12} /> <span>Agregar imagen</span>
            </button>
          </div>
          <div>
            <label  style={{  ...S.label }} className="adm-label" htmlFor="leccion-quizid">Quiz Vortex Academy (quizId)</label>
            <input
              id="leccion-quizid"
              type="text"
              value={lessonModal.lesson.quizId || ''}
              onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, quizId: e.target.value } })}
              className="adm-input"
              placeholder="Ej: qz_1700000000000 (dejar vacío para usar el quiz embebido)"
            />
            <p style={{ margin: '4px 0 0', fontSize: '0.66rem', color: '#94a3b8' }}>
              Si se indica un quizId, el quiz evaluativo y las flashcards de esta lección se tomarán de
              <code style={{ color: '#8a6a2e' }}> vortex_academy_quizzes/{'{jerarquia}/{cuerpo}/{especialidad}'}/{'{quizId}'}</code>.
            </p>
          </div>
          <div>
            <label style={S.label} htmlFor="leccion-kp">Puntos Clave (uno por línea)</label>
            <textarea
              id="leccion-kp"
              rows={4}
              value={(lessonModal.lesson.key_points || []).join('\n')}
              onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, key_points: e.target.value.split('\n').filter(Boolean) } })}
              style={{ ...S.input, resize: 'vertical', fontFamily: "'Inter', sans-serif" }}
              placeholder={'Artículo 1 de la Ley de Disciplina\nUso de la fuerza en instalaciones...'}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={S.label} htmlFor="leccion-pdf-name">Nombre del PDF</label>
              <input
                id="leccion-pdf-name"
                type="text"
                value={lessonModal.lesson.pdf_resources?.[0]?.name || ''}
                onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, pdf_resources: [{ name: e.target.value, url: lessonModal.lesson.pdf_resources?.[0]?.url || '', size: 'PDF' }] } })}
                className="adm-input"
                placeholder="Material_Leccion_1.pdf"
              />
            </div>
            <div>
              <label style={S.label} htmlFor="leccion-pdf-url">URL del PDF</label>
              <input
                id="leccion-pdf-url"
                type="url"
                value={lessonModal.lesson.pdf_resources?.[0]?.url || ''}
                onChange={(e) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, pdf_resources: [{ name: lessonModal.lesson.pdf_resources?.[0]?.name || 'Material.pdf', url: e.target.value, size: 'PDF' }] } })}
                className="adm-input"
                placeholder="https://..."
              />
            </div>
          </div>
          <div>
            <label  style={{  ...S.label }} className="adm-label">Quiz de la Lección (se suma a los puntos clave de la lección)</label>
            <QuestionEditor
              questions={lessonModal.lesson.quiz || []}
              onChange={(questions) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, quiz: questions } })}
            />
          </div>
          <div>
            <FlashcardsEditor
              flashcards={lessonModal.lesson.flashcards || []}
              onChange={(flashcards) => setLessonModal({ ...lessonModal, lesson: { ...lessonModal.lesson, flashcards } })}
            />
          </div>
        </ModalShell>
      )}

      {/* ── MODAL EXAMEN ── */}
      {examModal && (
        <ModalShell
          title={examModal.editing ? 'Editar Examen de la Lección' : 'Agregar Examen a la Lección'}
          icon={<FileQuestion size={16} style={{ color: '#f59e0b' }} />}
          onClose={() => setExamModal(null)}
          onSubmit={saveExam}
          submitLabel={examModal.editing ? 'Guardar Examen' : 'Agregar Examen'}
        >
          <div>
            <label style={S.label} htmlFor="examen-title">Título del Examen</label>
            <input
              autoFocus
              id="examen-title"
              type="text"
              value={examModal.exam.title}
              onChange={(e) => setExamModal({ ...examModal, exam: { ...examModal.exam, title: e.target.value } })}
              className="adm-input"
              placeholder="Ej: Examen de la Lección - Bloque A"
            />
          </div>
          <div>
            <label  style={{  ...S.label }} className="adm-label" htmlFor="examen-quizid">Quiz Vortex Academy (quizId, opcional)</label>
            <input
              id="examen-quizid"
              type="text"
              value={examModal.exam.quizId || ''}
              onChange={(e) => setExamModal({ ...examModal, exam: { ...examModal.exam, quizId: e.target.value } })}
              className="adm-input"
              placeholder="Ej: qz_1700000000000 (vacío = preguntas embebidas)"
            />
          </div>
          <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', lineHeight: 1.6 }}>
            Este examen se aprueba con <strong style={{ color: '#c5a059' }}>70% o más</strong>. El tema se considera completado
            cuando todos los exámenes de todas sus lecciones están aprobados, liberando el examen final.
          </p>
          <QuestionEditor
            questions={examModal.exam.questions || []}
            onChange={(questions) => setExamModal({ ...examModal, exam: { ...examModal.exam, questions } })}
          />
        </ModalShell>
      )}

      {/* ── MODAL EXAMEN FINAL DEL TEMA ── */}
      {finalTemaModal && (
        <ModalShell
          title={finalTemaModal.editing ? 'Editar Examen Final del Tema' : 'Agregar Examen Final del Tema'}
          icon={<Award size={16} style={{ color: '#34d399' }} />}
          onClose={() => setFinalTemaModal(null)}
          onSubmit={saveFinalTema}
          submitLabel={finalTemaModal.editing ? 'Guardar Examen Final' : 'Agregar Examen Final'}
        >
          <div>
            <label style={S.label} htmlFor="final-tema-title">Título</label>
            <input
              autoFocus
              id="final-tema-title"
              type="text"
              value={finalTemaModal.exam.title}
              onChange={(e) => setFinalTemaModal({ ...finalTemaModal, exam: { ...finalTemaModal.exam, title: e.target.value } })}
              className="adm-input"
            />
          </div>
          <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', lineHeight: 1.6 }}>
            Este examen final es por tema: se libera al alumno cuando aprueba todos los exámenes de las lecciones
            de <strong style={{ color: '#c5a059' }}>este tema</strong> (mínimo 70%). Al aprobarlo, el tema queda
            completado y suma para su certificado.
          </p>
          <QuestionEditor
            questions={finalTemaModal.exam.questions || []}
            onChange={(questions) => setFinalTemaModal({ ...finalTemaModal, exam: { ...finalTemaModal.exam, questions } })}
          />
        </ModalShell>
      )}
    </div>
  );
};
